import React, { useMemo } from 'react'
import CanvasLogo from '../../../assets/icons/canvas-logo.svg'
import {
  getEnrollmentDate,
  shouldInferLevelComplete,
  getLevelKeyFromCode,
  buildLevelTrackerData
} from './utils'
import { LEVEL } from './utils/constants/levels'
import { LevelTrackerSection, RequirementsSection } from './ProgressTrackerContent'
import SuspendedProgressTracker from './SuspendedProgressTracker'
import {
  Container,
  Header,
  Title,
  AssociateHeader,
  Divider,
  ProgramInfoItem,
  ProgramInfoLabel,
  ProgramInfoLink,
  PortalButton,
  PortalButtonText
} from './Styles/ProgressTracker.styles'

const ProgressTracker = ({
  levelState,
  activeLevel = LEVEL.QUALIFYING,
  onLevelClick,
  programHistory = [],
  prospect,
  currentLevelCode,
  isSuspended = false
}) => {
  const { patRequirement = null, shouldShowPATWarning = false } = levelState || {}

  const levels = useMemo(() => {
    const baseLevels = buildLevelTrackerData(levelState, activeLevel, programHistory, currentLevelCode)
    
    return baseLevels.map(level => ({
      ...level,
      hasIncompleteRequirements: level.key === LEVEL.QUALIFYING && shouldShowPATWarning
    }))
  }, [levelState, activeLevel, programHistory, currentLevelCode, shouldShowPATWarning])

  const currentProgramLevel = useMemo(() => 
    programHistory?.find(p => getLevelKeyFromCode(p.levelCode) === activeLevel),
    [programHistory, activeLevel]
  )

  const shouldShowLevelCompleteBanner = useMemo(() =>
    shouldInferLevelComplete(activeLevel, programHistory, currentLevelCode, levelState, prospect),
    [activeLevel, programHistory, currentLevelCode, levelState, prospect]
  )

  const currentLevel = levelState[activeLevel]
  const enrollmentDate = getEnrollmentDate(programHistory)
  const levelCompletionDate = currentProgramLevel?.endDate || null
  const patRequirementWithWarning = activeLevel === LEVEL.QUALIFYING && shouldShowPATWarning ? patRequirement : null
  const additionalRequirements = activeLevel !== LEVEL.QUALIFYING && patRequirement ? [patRequirement] : []

  if (isSuspended) {
    return (
      <SuspendedProgressTracker
        levels={levels}
        enrollmentDate={enrollmentDate}
        currentLevel={currentLevel}
        levelCompletionDate={levelCompletionDate}
        patRequirementWithWarning={patRequirementWithWarning}
        additionalRequirements={additionalRequirements}
        activeLevel={activeLevel}
        shouldShowPATWarning={shouldShowPATWarning}
        shouldShowLevelCompleteBanner={shouldShowLevelCompleteBanner}
        onLevelClick={onLevelClick}
      />
    )
  }

  return (
    <Container>
      <Header>
        <Title>My Path to Membership</Title>
        {!!enrollmentDate && (
          <AssociateHeader>
            <ProgramInfoItem>
              <ProgramInfoLabel>Associate Program</ProgramInfoLabel>
              <ProgramInfoLink>Details ›</ProgramInfoLink>
            </ProgramInfoItem>
            <Divider />
            <ProgramInfoItem>
              <ProgramInfoLabel>Enrolled: {enrollmentDate}</ProgramInfoLabel>
              <ProgramInfoLink>Guidelines ›</ProgramInfoLink>
            </ProgramInfoItem>
          </AssociateHeader>
        )}
      </Header>

      <LevelTrackerSection 
        levels={levels}
        onLevelClick={onLevelClick}
        isSuspended={false}
      />

      <RequirementsSection
        shouldShowLevelCompleteBanner={shouldShowLevelCompleteBanner}
        currentLevel={currentLevel}
        levelCompletionDate={levelCompletionDate}
        patRequirementWithWarning={patRequirementWithWarning}
        additionalRequirements={additionalRequirements}
        activeLevel={activeLevel}
        shouldShowPATWarning={shouldShowPATWarning}
        onLevelClick={onLevelClick}
      />

      <PortalButton>
        <img src={CanvasLogo} alt="Canvas Logo" />
        <PortalButtonText>Education Portal</PortalButtonText>
      </PortalButton>
    </Container>
  )
}

export default ProgressTracker
